import type { ScanRecord } from '../services/scanService';

export interface SkinLovingFood {
  name: string;
  description: string;
  icon: 'fish' | 'salad' | 'apple' | 'nut' | 'leaf' | 'grain' | 'berry' | 'seed';
}

/**
 * Generates personalized skin-loving foods based on user's latest scan
 */
export function generateSkinLovingFoods(
  latestScan: ScanRecord | null,
  dietaryPreference?: 'vegetarian' | 'non-vegetarian'
): SkinLovingFood[] {
  const foods: SkinLovingFood[] = [];
  
  if (!latestScan?.analysis) {
    // Default foods if no scan available
    if (dietaryPreference === 'vegetarian') {
      foods.push({
        name: 'Omega-3 Rich Foods',
        description: 'Flaxseeds, walnuts, chia seeds — reduce inflammation and support skin barrier',
        icon: 'seed'
      });
    } else {
      foods.push({
        name: 'Omega-3 Rich Foods',
        description: 'Salmon, walnuts, chia seeds — reduce inflammation and support skin barrier',
        icon: 'fish'
      });
    }
    foods.push({
      name: 'Leafy Greens',
      description: 'Spinach, kale, arugula — packed with antioxidants and vitamins A, C, E',
      icon: 'salad'
    });
    foods.push({
      name: 'Berries & Fruits',
      description: 'Blueberries, strawberries, papaya — high in vitamin C for collagen production',
      icon: 'berry'
    });
    foods.push({
      name: 'Nuts & Seeds',
      description: 'Almonds, sunflower seeds — vitamin E protects skin from oxidative stress',
      icon: 'nut'
    });
    return foods;
  }

  const analysis = latestScan.analysis;
  const glowScore = analysis.skinHealthIndex ?? latestScan.glowScore ?? 70;
  const acneSeverity = (analysis.acneAnalysis?.overallSeverity ?? analysis.acneSeverity ?? 'mild') as string;
  const estimatedPH = analysis.estimatedPH ?? analysis.acneAnalysis?.estimatedPH;
  const isInflamed = analysis.acneAnalysis?.acneType?.inflammatory === 'moderate' || 
                     analysis.acneAnalysis?.acneType?.inflammatory === 'pronounced' ||
                     acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe';

  // Use a seed based on scan parameters to ensure variety
  // Include scanId or timestamp to ensure different scans produce different results
  const scanIdHash = latestScan?.scanId ? latestScan.scanId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 0;
  const timestampHash = latestScan?.timestamp ? new Date(latestScan.timestamp).getTime() % 1000 : 0;
  const seed = Math.floor((glowScore * 100 + (estimatedPH || 5.0) * 10 + 
    (acneSeverity === 'severe' ? 100 : acneSeverity === 'moderate' ? 50 : 0) + scanIdHash + timestampHash) % 10000);

  // Pool of all possible foods
  const allFoods: SkinLovingFood[] = [];

  // Omega-3 foods (different for vegetarians)
  if (dietaryPreference === 'vegetarian') {
    allFoods.push({
      name: 'Omega-3 Rich Foods',
      description: 'Flaxseeds, walnuts, chia seeds — reduce inflammation and support skin barrier',
      icon: 'seed'
    });
    allFoods.push({
      name: 'Plant-Based Omega-3',
      description: 'Hemp seeds, chia seeds, walnuts — anti-inflammatory and skin barrier support',
      icon: 'seed'
    });
  } else {
    allFoods.push({
      name: 'Omega-3 Rich Foods',
      description: 'Salmon, walnuts, chia seeds — reduce inflammation and support skin barrier',
      icon: 'fish'
    });
    allFoods.push({
      name: 'Fatty Fish',
      description: 'Mackerel, sardines, salmon — high in omega-3s for reduced inflammation',
      icon: 'fish'
    });
  }

  // Anti-inflammatory foods (especially important for inflamed acne)
  if (isInflamed) {
    allFoods.push({
      name: 'Turmeric & Ginger',
      description: 'Turmeric, ginger, green tea — powerful anti-inflammatory compounds',
      icon: 'leaf'
    });
    allFoods.push({
      name: 'Dark Leafy Greens',
      description: 'Spinach, kale, Swiss chard — rich in antioxidants to calm inflammation',
      icon: 'salad'
    });
  } else {
    allFoods.push({
      name: 'Leafy Greens',
      description: 'Spinach, kale, arugula — packed with antioxidants and vitamins A, C, E',
      icon: 'salad'
    });
  }

  // Vitamin C foods (important for collagen and healing)
  if (glowScore < 70 || acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
    allFoods.push({
      name: 'Vitamin C Powerhouses',
      description: 'Oranges, bell peppers, kiwi — boost collagen production and skin healing',
      icon: 'berry'
    });
    allFoods.push({
      name: 'Citrus Fruits',
      description: 'Grapefruit, oranges, lemons — high vitamin C for skin repair',
      icon: 'berry'
    });
  } else {
    allFoods.push({
      name: 'Berries & Fruits',
      description: 'Blueberries, strawberries, papaya — high in vitamin C for collagen production',
      icon: 'berry'
    });
  }

  // Zinc-rich foods (important for acne)
  if (acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
    if (dietaryPreference === 'vegetarian') {
      allFoods.push({
        name: 'Zinc-Rich Plant Foods',
        description: 'Pumpkin seeds, lentils, chickpeas — zinc supports acne healing',
        icon: 'seed'
      });
    } else {
      allFoods.push({
        name: 'Zinc-Rich Foods',
        description: 'Oysters, lean beef, pumpkin seeds — zinc supports acne healing',
        icon: 'nut'
      });
    }
  }

  // Probiotic foods (for gut-skin connection)
  if (glowScore < 60) {
    if (dietaryPreference === 'vegetarian') {
      allFoods.push({
        name: 'Plant-Based Probiotics',
        description: 'Fermented foods, kimchi, sauerkraut — support gut health and clear skin',
        icon: 'leaf'
      });
    } else {
      allFoods.push({
        name: 'Probiotic Foods',
        description: 'Greek yogurt, kefir, fermented foods — support gut health and clear skin',
        icon: 'grain'
      });
    }
  }

  // Antioxidant-rich foods
  allFoods.push({
    name: 'Antioxidant-Rich Berries',
    description: 'Blueberries, blackberries, raspberries — fight free radicals and protect skin',
    icon: 'berry'
  });
  allFoods.push({
    name: 'Colorful Vegetables',
    description: 'Bell peppers, carrots, sweet potatoes — beta-carotene and antioxidants',
    icon: 'salad'
  });

  // Vitamin E foods
  allFoods.push({
    name: 'Nuts & Seeds',
    description: 'Almonds, sunflower seeds — vitamin E protects skin from oxidative stress',
    icon: 'nut'
  });
  allFoods.push({
    name: 'Healthy Fats',
    description: 'Avocado, almonds, olive oil — support skin barrier and hydration',
    icon: 'nut'
  });

  // Whole grains (for stable blood sugar)
  if (glowScore < 70) {
    allFoods.push({
      name: 'Whole Grains',
      description: 'Quinoa, brown rice, oats — stable blood sugar reduces acne triggers',
      icon: 'grain'
    });
  }

  // Beta-carotene foods (for skin healing)
  if (acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
    allFoods.push({
      name: 'Beta-Carotene Rich',
      description: 'Sweet potatoes, carrots, mangoes — support skin healing and repair',
      icon: 'apple'
    });
  }

  // Simple seeded shuffle function
  const seededShuffle = <T,>(array: T[], seed: number): T[] => {
    const shuffled = [...array];
    let currentSeed = seed;
    for (let i = shuffled.length - 1; i > 0; i--) {
      currentSeed = (currentSeed * 9301 + 49297) % 233280;
      const j = Math.floor((currentSeed / 233280) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Shuffle and select 4 foods
  const shuffled = seededShuffle(allFoods, seed);
  return shuffled.slice(0, 4);
}
